import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { userGetCurrent } from '@uncinc/react-drupal-core';

import { webformSubmit } from '../../store/actions/Webform';

import {
  Button,
  CoreLink,
  Main,
  ToggleGroup,
} from '../../components';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useRoutes from '../../hooks/useRoutes';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import { isEligibleForStudy, getWebformValues } from '../../config/buddyCoScreeningQuestions';
import type { BuddyCoAnswers } from '../OnboardingBuddyCo';

import './style.scss';

type LocationState = { answers?: BuddyCoAnswers };

const PageOnboardingBuddyCoConsent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { state: routeState } = useLocation<LocationState>();
  const { logout, joyride } = useRoutes();
  const { buildClass, joinClasses } = useClasses('PageOnboardingBuddyCoConsent', 'PageOnboardingBuddyCoConsent--default');

  const dispatch = useTypedDispatch();
  const [consent, setConsent] = useState<string | undefined>(undefined);
  const [showError, setShowError] = useState(false);

  const currentUserId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], 0));
  const userEmail = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', currentUserId, 'mail', 0, 'value'], ''));

  useEffect(() => {
    const answers = routeState?.answers;
    if (!answers || !isEligibleForStudy(answers)) {
      history.replace(joyride);
    }
  }, [history, joyride, routeState]);

  const handleSubmit = () => {
    if (consent === undefined) {
      setShowError(true);
      return;
    }

    const answers = routeState?.answers ?? {};
    const values = getWebformValues(answers, consent === 'yes');
    dispatch(webformSubmit('buddy_co_screening', values))
      .then(() => dispatch(userGetCurrent()))
      .catch(() => {})
      .finally(() => history.push(joyride));
  };

  return (
    <Page
      className={buildClass()}
      hideMenuMain
      hideChrome
      title={t('buddy_co_consent.page_title')}
      headerContent={(
        <CoreLink
          to={logout}
          title={t('buttons.logout')}
          className={buildClass('header-logout')}
        />
      )}
    >
      <Container>
        <div className="row">
          <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 py-4 py-md-12">
            <Main className={buildClass('content')}>
              <h1 className={buildClass('title')}>{t('buddy_co_consent.title')}</h1>
              <p className={joinClasses(buildClass('body'), 'body--large')}>
                {t('buddy_co_consent.body_1')}
              </p>
              <p className={joinClasses(buildClass('body'), 'body--large')}>
                {t('buddy_co_consent.body_2')}
              </p>
              <p className={joinClasses(buildClass('body'), 'body--large')}>
                {t('buddy_co_consent.body_3')}
              </p>

              <ToggleGroup
                id="buddy-co-consent"
                label={t('buddy_co_consent.question')}
                type="binary"
                options={[
                  { label: t('buddy_co_screening.options.yes'), value: 'yes' },
                  { label: t('buddy_co_screening.options.no'), value: 'no' },
                ]}
                selectedValue={consent}
                onChange={(val) => {
                  setConsent(val);
                  setShowError(false);
                }}
                error={showError ? t('buddy_co_consent.validation') : undefined}
              />

              {consent === 'yes' && (
                <p className={buildClass('consent-given')}>
                  <Trans
                    i18nKey="buddy_co_consent.consent_given"
                    values={{ email: userEmail }}
                    components={{ bold: <strong /> }}
                  />
                </p>
              )}

              <div className={buildClass('submit')}>
                <Button onClick={handleSubmit}>
                  {t('buddy_co_consent.submit')}
                </Button>
              </div>
            </Main>
          </div>
        </div>
      </Container>
    </Page>
  );
};

export default PageOnboardingBuddyCoConsent;
